import { Rating, VerifiedBadge } from "../components/Rating";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n, catName } from "../lib/i18n";
import { Button } from "../components/ui/button";
import CategoryIcon from "../components/CategoryIcon";
import { CheckCircle2, Loader2, ArrowRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { SITE_URL, OG_IMAGE } from "../lib/seo";

export function MockPaymentPage() {
  useSEO({ title: "Payment | Refine Touch USA", robots: "noindex, follow" });
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { t } = useI18n();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("idle");

  const confirm = async () => {
    setStatus("processing");
    await new Promise((r) => setTimeout(r, 1200));
    await api.post(`/payments/mock-complete/${sessionId}`);
    setStatus("done");
    setTimeout(() => nav("/dashboard"), 1500);
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6">
      <div className="max-w-md w-full bg-white border border-stone-200 rounded-3xl p-10 text-center">
        <div className="eyebrow text-amber-700 mb-3">{t("demo_mode")}</div>
        <h1 className="font-serif text-4xl">{t("mock_pay_title")}</h1>
        <p className="text-stone-600 text-sm mt-3">{t("mock_pay_desc")}</p>
        {status === "idle" && <Button data-testid="mock-pay" onClick={confirm} className="mt-8 w-full h-11 rounded-xl bg-stone-900 text-stone-50">{t("confirm_pay")}</Button>}
        {status === "processing" && <div className="mt-8 flex items-center justify-center gap-2 text-stone-600"><Loader2 size={18} className="animate-spin" />{t("processing")}</div>}
        {status === "done" && <div className="mt-8 flex flex-col items-center gap-2 text-emerald-700"><CheckCircle2 size={32} />{t("pay_confirmed")}</div>}
      </div>
    </div>
  );
}

export function CategoriesPage() {
  const [cats, setCats] = useState([]);
  const { lang, t } = useI18n();
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  useSEO({
    title: "Service Categories | French Professionals in the USA | Refine Touch USA",
    description: "Browse every service category on Refine Touch USA — French chefs, tutors, real estate agents, designers, photographers, beauty and home services across the United States.",
    canonical: `${SITE_URL}/categories`,
    ogImage: OG_IMAGE,
    jsonLd: [{
      "@context": "https://schema.org", "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Categories", item: `${SITE_URL}/categories` },
      ],
    }, {
      "@context": "https://schema.org", "@type": "ItemList",
      itemListElement: cats.map((c, i) => ({ "@type": "ListItem", position: i + 1, url: `${SITE_URL}/categories/${c.slug}`, name: catName(c, lang) })),
    }],
  });
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl">{t("cats_title")}</h1>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        {cats.map((c) => (
          <Link key={c.id} to={`/categories/${c.slug}`} data-testid={`category-${c.slug}`} className="service-card relative aspect-[4/5] rounded-2xl overflow-hidden block">
            <img src={c.image} className="w-full h-full object-cover" alt="" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent" />
            <div className="absolute top-4 left-4 h-11 w-11 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-stone-900 shadow-md ring-1 ring-white/60">
              <CategoryIcon name={c.icon} size={20} />
            </div>
            <div className="absolute bottom-4 left-4 text-white font-serif text-xl">{catName(c, lang)}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function ProvidersPage() {
  const [provs, setProvs] = useState([]);
  const { t } = useI18n();
  useEffect(() => { api.get("/providers").then((r) => setProvs(r.data)); }, []);
  useSEO({
    title: "French Professionals & Providers in the USA | Refine Touch USA",
    description: "Meet every French-speaking professional on Refine Touch USA — chefs, tutors, real estate agents, designers, photographers, and more, serving clients across the United States.",
    canonical: `${SITE_URL}/providers`,
    ogImage: OG_IMAGE,
  });
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="font-serif text-4xl">{t("our_providers")}</h1>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        {provs.map((p) => (
          <Link key={p.id} to={`/providers/${p.id}`} className="service-card bg-white border border-stone-200 rounded-2xl p-6 block">
            <div className="flex items-center gap-4">
              <img src={p.avatar} className="w-16 h-16 rounded-full object-cover" alt={p.name} />
              <div>
                <div className="font-serif text-xl">{p.name}</div>
                <div className="text-xs text-stone-500">{p.location}</div>
              </div>
            </div>
            <p className="text-sm text-stone-600 mt-3 line-clamp-2">{p.bio}</p>
            <div className="text-xs text-stone-500 mt-3 flex items-center gap-2 flex-wrap">{p.service_count} {t("services_count")} · <Rating rating={p.rating} count={p.review_count} size={12} /> <VerifiedBadge compact /></div>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function HowItWorksPage() {
  const { t } = useI18n();
  useSEO({
    title: "How It Works | Refine Touch USA",
    description: "Learn how Refine Touch USA connects U.S. clients with French professionals across the United States, from browsing services to booking.",
    canonical: `${SITE_URL}/how-it-works`,
    ogImage: OG_IMAGE,
  });
  const HERO = "https://customer-assets-wrfwihn1.emergentagent.net/job_service-marketplace-447/artifacts/exi9b0e2_lance-asper-pAWY7xrsLwc-unsplash.webp";
  const CUST_BG = "https://images.unsplash.com/photo-1512909006721-3d6018887383?auto=format&fit=crop&w=1400&q=75"; // Ocean Drive Art Deco
  const PROV_BG = "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=75"; // creative collab
  const CTA_BG  = "https://customer-assets-wrfwihn1.emergentagent.net/job_service-marketplace-447/artifacts/9o980ajd_shawn-henley-3CQQlwWpn70-unsplash.webp";
  return (
    <div>
      {/* Hero banner */}
      <section className="relative h-[46vh] min-h-[320px] w-full overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/25 to-black/55" />
        <div className="relative z-10 h-full flex items-end pb-14">
          <div className="max-w-5xl mx-auto px-6 lg:px-10 w-full">
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] drop-shadow-[0_3px_20px_rgba(0,0,0,0.5)]">{t("how_page_title")}</h1>
            <p className="text-white/90 mt-3 max-w-xl text-sm sm:text-base drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]">{t("how_subtitle")}</p>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid md:grid-cols-2 gap-8">
          {/* FOR CUSTOMERS card */}
          <div className="relative rounded-3xl overflow-hidden bg-stone-900 text-stone-50 min-h-[560px] flex flex-col">
            <img src={CUST_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
            <div className="absolute inset-0 bg-gradient-to-b from-stone-900/60 via-stone-900/85 to-stone-900" />
            <div className="relative p-8 lg:p-10 flex flex-col flex-1">
              <div className="eyebrow text-white/60">{t("for_customers")}</div>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/services" data-testid="hw-customer-cta"><Button className="rounded-full h-11 px-6 bg-white text-stone-900 hover:bg-stone-100 btn-pill">{t("hw_customer_cta")} <ArrowRight size={14} className="ml-2" /></Button></Link>
                <Link to="/register" data-testid="hw-customer-signup"><Button variant="outline" className="rounded-full h-11 px-6 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white btn-pill">{t("hw_customer_signup")}</Button></Link>
              </div>
              <div className="mt-10 space-y-8 flex-1">
                {[[t("hw_c1_t"), t("hw_c1_d")], [t("hw_c2_t"), t("hw_c2_d")], [t("hw_c3_t"), t("hw_c3_d")]].map(([tt, d], i) => (
                  <div key={i} className="flex gap-5">
                    <div className="font-serif text-4xl text-white/30 leading-none w-12 shrink-0">0{i+1}</div>
                    <div>
                      <div className="font-serif text-2xl">{tt}</div>
                      <p className="text-white/75 mt-1 text-sm leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* FOR PROVIDERS card */}
          <div className="relative rounded-3xl overflow-hidden bg-stone-50 text-stone-900 min-h-[560px] flex flex-col border border-stone-200">
            <img src={PROV_BG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/80 to-white" />
            <div className="relative p-8 lg:p-10 flex flex-col flex-1">
              <div className="eyebrow text-stone-500">{t("for_providers")}</div>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/register?role=provider" data-testid="hw-provider-cta"><Button className="rounded-full h-11 px-6 bg-stone-900 text-stone-50 hover:bg-stone-800 btn-pill">{t("hw_provider_cta")} <ArrowRight size={14} className="ml-2" /></Button></Link>
                <Link to="/login" data-testid="hw-provider-signin"><Button variant="outline" className="rounded-full h-11 px-6 border-stone-300 btn-pill">{t("hw_provider_signin")}</Button></Link>
              </div>
              <div className="mt-10 space-y-8 flex-1">
                {[[t("hw_p1_t"), t("hw_p1_d")], [t("hw_p2_t"), t("hw_p2_d")], [t("hw_p3_t"), t("hw_p3_d")]].map(([tt, d], i) => (
                  <div key={i} className="flex gap-5">
                    <div className="font-serif text-4xl text-stone-300 leading-none w-12 shrink-0">0{i+1}</div>
                    <div>
                      <div className="font-serif text-2xl">{tt}</div>
                      <p className="text-stone-600 mt-1 text-sm leading-relaxed">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Closing banner */}
        <section className="relative mt-16 rounded-3xl overflow-hidden">
          <img src={CTA_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/45 to-black/30" />
          <div className="relative p-10 lg:p-14 text-white">
            <h3 className="font-serif text-4xl sm:text-5xl leading-tight max-w-2xl drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]">{t("banner_florida")}</h3>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/services"><Button className="rounded-full h-11 px-6 bg-white text-stone-900 hover:bg-stone-100 btn-pill">{t("cta_customer")} <ArrowRight size={14} className="ml-2" /></Button></Link>
              <Link to="/register?role=provider"><Button variant="outline" className="rounded-full h-11 px-6 border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white btn-pill">{t("cta_provider")}</Button></Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export function ProfilePage() {
  useSEO({ title: "Profile | Refine Touch USA", robots: "noindex, follow" });
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const { t } = useI18n();
  useEffect(() => { api.get("/auth/me").then((r) => { setMe(r.data); setForm(r.data); }); }, []);
  const save = async () => {
    await api.put("/auth/me", { name: form.name, phone: form.phone, location: form.location, bio: form.bio });
    setMe(await api.get("/auth/me").then((r) => r.data));
  };
  if (!me) return <div className="p-10">{t("loading")}</div>;
  return (
    <div className="max-w-2xl mx-auto p-10">
      <h1 className="font-serif text-4xl">{t("my_profile")}</h1>
      <div className="mt-8 space-y-4 bg-white border border-stone-200 rounded-2xl p-6">
        <div><label className="text-sm text-stone-600">{t("name")}</label><input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.name || ""} onChange={(e) => setForm({...form, name: e.target.value})} /></div>
        <div><label className="text-sm text-stone-600">{t("phone")}</label><input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.phone || ""} onChange={(e) => setForm({...form, phone: e.target.value})} /></div>
        <div><label className="text-sm text-stone-600">{t("city")}</label><input className="w-full border rounded-lg px-3 py-2 mt-1" value={form.location || ""} onChange={(e) => setForm({...form, location: e.target.value})} /></div>
        <div><label className="text-sm text-stone-600">{t("bio")}</label><textarea rows={4} className="w-full border rounded-lg px-3 py-2 mt-1" value={form.bio || ""} onChange={(e) => setForm({...form, bio: e.target.value})} /></div>
        <Button onClick={save} className="bg-stone-900 text-stone-50">{t("save")}</Button>
      </div>
    </div>
  );
}
